import {
  LitElement,
  paths,
  html,
  unsafeHTML,
  css,
  utils,
  t,
  ifDefined,
} from '@hh/global.js'
const { externalVideoEmbedSrc } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    playing: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    muted: {
      type: Boolean,
      state: true,
      reflect: true,
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  constructor() {
    super()
    this._descId = `hh-video-desc-${Math.random().toString(36).slice(2, 11)}`
  }

  get trimmedTitle() {
    return (this.title || '').trim()
  }

  get trimmedDescription() {
    return (this.description || '').trim()
  }

  get videoAccessibleName() {
    const tTitle = this.trimmedTitle
    const tDesc = this.trimmedDescription
    if (tTitle) return tTitle
    if (tDesc) return tDesc
    return t('accessibility.play_video')
  }

  get showDescriptionBy() {
    const tTitle = this.trimmedTitle
    const tDesc = this.trimmedDescription
    return Boolean(tTitle && tDesc && tDesc !== tTitle)
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.muted = true
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
  }

  firstUpdated() {
    if (this.host === 'vimeo') {
      this.videoElement = new Vimeo.Player(this.videoElement)
    }
  }

  handlePlayClick() {
    if (this.playing == false || this.playing == undefined) {
      this.playing = true
      let overlay = this.renderRoot?.querySelector('.video-overlay')
      if (overlay) overlay.style.display = 'none'
      this.videoElement.play()
    } else {
      this.playing = false
      this.videoElement.pause()
    }
  }

  generatePosterWithText() {
    if (!this.posterImage) return html``
    return html`
      <div class="video-overlay">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        <button
          class="video-poster-button"
          @click="${this.handlePlayClick}"
          aria-label="${t('accessibility.play', 'Play')}"
        >
          ${unsafeHTML(paths.play || html`►`)}
        </button>
        <img src="${this.posterImage}" role="presentation" alt="" />
      </div>
    `
  }

  handleVideoPlayback(e) {
    e.preventDefault()
    e.stopPropagation()
    e.preventDefault()
    this.playing = !this.playing

    this.playing ? this.videoElement.play() : this.videoElement.pause()

    const pauseBtn = this.renderRoot?.querySelector('.js-pause-btn')
    const playBtn = this.renderRoot?.querySelector('.js-play-btn')
    const buttonWrap = this.renderRoot?.querySelector('.video-overlay-button')

    pauseBtn.classList.toggle('hidden', !this.playing)
    playBtn.classList.toggle('hidden', this.playing)
    buttonWrap.ariaLabel = this.playing
      ? t('accessibility.pause_video')
      : t('accessibility.play_video')
  }

  generateVideoPlaybackTrigger() {
    return html`
      <div class="video-overlay">
        <button
          class="video-overlay-button"
          @click="${this.handleVideoPlayback}"
          aria-label="${t('accessibility.pause_video')}"
        >
          <span class="js-pause-btn pause-btn"
            >${unsafeHTML(paths.pause) || html`►`}</span
          >
          <span class="js-play-btn play-btn hidden"
            >${unsafeHTML(paths.play) || html`►`}</span
          >
        </button>
      </div>
    `
  }

  handleVolumeToggle(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.videoElement) {
      this.videoElement.muted = !this.muted
      this.muted = !this.muted
    }
  }

  generateVideoMuteTrigger() {
    return html`
      <button
        class="video-overlay-button mute"
        @click="${this.handleVolumeToggle}"
        aria-label="${this.muted
          ? t('accessibility.sound_video')
          : t('accessibility.mute_video')}"
      >
        ${this.muted ? unsafeHTML(paths.mute) : unsafeHTML(paths.sound)}
      </button>
    `
  }

  renderVideo() {
    const showDesc = this.showDescriptionBy
    const tDesc = this.trimmedDescription
    return html`
      ${showDesc
        ? html`<div id="${this._descId}" class="visually-hidden">${tDesc}</div>`
        : html``}
      <video
        id="video-element"
        onloadedmetadata="this.muted = true"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="none"
        muted="muted"
        aria-label="${this.videoAccessibleName}"
        aria-describedby="${ifDefined(showDesc ? this._descId : undefined)}"
      >
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    const showDesc = this.showDescriptionBy
    const tDesc = this.trimmedDescription
    return html`
      ${showDesc
        ? html`<div id="${this._descId}" class="visually-hidden">${tDesc}</div>`
        : html``}
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop
        )}"
        title="${this.videoAccessibleName}"
        aria-describedby="${ifDefined(showDesc ? this._descId : undefined)}"
      >
      </iframe>
    `
  }

  render() {
    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay
        ? this.generateVideoPlaybackTrigger()
        : this.generatePosterWithText()}
      ${this.generateVideoMuteTrigger()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .video-overlay img {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      filter: brightness(0.5);
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border-radius: 60px;
      bottom: var(--hh-space-sm);
      cursor: pointer;
      width: 60px;
      height: 60px;
      padding: 1rem;
      display: flex;
      justify-content: center;
      position: absolute;
      right: var(--hh-space-sm);
      z-index: 1;
    }
    .video-poster-button svg {
      position: relative;
      width: 30px;
      height: 30px;
      fill: white;
      stroke: white;
    }
    .video-overlay-button {
      align-items: center;
      background: transparent;
      border: 0;
      bottom: 2rem;
      cursor: pointer;
      padding: 0;
      display: flex;
      justify-content: center;
      position: absolute;
      right: 1rem;
      z-index: 1;
    }

    .play-btn,
    .pause-btn {
      position: absolute;
      transform: translate(-50%, -50%);
      width: 30px;
      height: 30px;
    }

    .play-btn.hidden,
    .pause-btn.hidden {
      border: 0 !important;
      clip: rect(1px, 1px, 1px, 1px) !important;
      -webkit-clip-path: inset(50%) !important;
      clip-path: inset(50%) !important;
      height: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      padding: 0 !important;
      position: absolute !important;
      width: 1px !important;
      white-space: nowrap !important;
    }

    .video-overlay-button.mute {
      right: 5rem;
    }

    @media screen and (min-width: 750px) {
      .video-overlay-button {
        right: 2rem;
      }

      .video-overlay-button.mute {
        right: 6rem;
      }
    }

    .js-pause-btn {
      display: flex;
    }

    .visually-hidden {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
  `
}

window.customElements.define('hh-adaptive-video', AdaptiveVideo)
